import React, { useMemo, useState } from 'react'
import { Modal } from '@enamelai/design-system'
import { useNotificationToast } from '@context/NotificationToastContext'
import type { MembershipPageMember } from '@dataTypes/membership.type'
import { updateMembershipMemberStatus } from '@store/actions/membership.action'
import { useAppDispatch } from '@store/hooks'
import {
  createFailedToast,
  createMembershipReactivatedToast,
} from '@utils/notificationToast.util'
import './ReactivateMembershipModal.css'

const HOLD_BUSINESS_DAYS = 14

type ReactivateMembershipModalProps = {
  open: boolean
  member?: MembershipPageMember | null
  holdStartedAt?: Date | null
  onClose: () => void
  onReactivated?: (member: MembershipPageMember) => void
}

const addBusinessDays = (start: Date, days: number): Date => {
  const date = new Date(start.getFullYear(), start.getMonth(), start.getDate())
  let remaining = days

  while (remaining > 0) {
    date.setDate(date.getDate() + 1)
    const weekday = date.getDay()
    if (weekday !== 0 && weekday !== 6) {
      remaining -= 1
    }
  }

  return date
}

const startOfDay = (value: Date): Date =>
  new Date(value.getFullYear(), value.getMonth(), value.getDate())

const parseStoredDate = (value?: string | null): Date | null => {
  if (!value) return null
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (!match) return null
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

const formatResumeDate = (value: Date): string =>
  new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(value)

const ReactivateMembershipModal: React.FC<ReactivateMembershipModalProps> = ({
  open,
  member,
  holdStartedAt,
  onClose,
  onReactivated,
}) => {
  const dispatch = useAppDispatch()
  const { showToast } = useNotificationToast()
  const [saving, setSaving] = useState(false)
  const memberName = member?.name ?? 'Patient'

  const holdStart = useMemo(() => {
    if (holdStartedAt) return holdStartedAt
    if (member?.status !== 'On hold') return null
    return parseStoredDate(member.statusChangedAt)
  }, [holdStartedAt, member?.status, member?.statusChangedAt])
  const earliestDate = useMemo(
    () => addBusinessDays(holdStart ?? new Date(), HOLD_BUSINESS_DAYS),
    [holdStart],
  )
  const canReactivate =
    holdStart == null || startOfDay(new Date()) >= startOfDay(earliestDate)
  const resumeDate = canReactivate ? new Date() : earliestDate
  const resumeLabel = formatResumeDate(
    canReactivate ? resumeDate : earliestDate,
  )

  const handleReactivate = (): void => {
    if (!member || saving || !canReactivate) return

    setSaving(true)
    void dispatch(
      updateMembershipMemberStatus({ id: member.id, status: 'active' }),
    ).then((result) => {
      setSaving(false)

      if (updateMembershipMemberStatus.rejected.match(result)) {
        showToast(
          createFailedToast(
            'Unable to reactivate membership',
            result.payload || 'Failed to reactivate membership.',
          ),
        )
        return
      }

      showToast(createMembershipReactivatedToast(memberName))
      onReactivated?.(result.payload)
      onClose()
    })
  }

  if (!canReactivate) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        size="sm"
        title="Unable to Reactivate"
        className="reactivate-membership-modal"
        bodyClassName="reactivate-membership-modal__body"
        primaryLabel="Okay"
        secondaryLabel=""
        onPrimaryClick={onClose}
      >
        <div className="reactivate-membership-modal__copy reactivate-membership-modal__copy--blocked">
          <p>
            Memberships must remain on-hold for at least 14 business days before
            reactivating.
            <br />
            <br />
            You can reactivate this plan on or after{' '}
            <strong>{formatResumeDate(earliestDate)}.</strong>
          </p>
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title="Reactivate membership"
      className="reactivate-membership-modal"
      primaryLabel="Reactivate plan"
      primaryDisabled={saving}
      secondaryLabel="Cancel"
      onPrimaryClick={handleReactivate}
      onSecondaryClick={onClose}
    >
      <div className="reactivate-membership-modal__copy">
        <p>
          {memberName}
          {'\u2019'}s membership benefits will be available on{' '}
          <strong>{resumeLabel}.</strong>
        </p>
        <p className="reactivate-membership-modal__note">Note:</p>
        <ul className="reactivate-membership-modal__notes">
          <li>Latest billing details will be used.</li>
          <li>The reactivation payment will be billed on {resumeLabel}.</li>
          <li>
            Perks and benefits will be available after payments are confirmed.
          </li>
        </ul>
        <p className="reactivate-membership-modal__question">
          Would you like to proceed?
        </p>
      </div>
    </Modal>
  )
}

export default ReactivateMembershipModal
